import { getErrorMessage } from "utils/error";
import { FC, useEffect, useState } from "react";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  CheckboxCard,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Input
} from "shared/components";
import { Trash02, XClose } from "untitledui-js-base";
import addressApi from "api/landlord/address";
import { Property } from "types/landlord/address";
import { useForm } from "shared/hooks/use-form";
import { usePlacesWidget } from "react-google-autocomplete";
import { googlePlaceToAddress } from "shared/addressUtils";
import config from "config/config";
import { useVerificationTemplatesController } from "views/verification-templates/controller";
import { useAuthContext } from "providers/Auth";
import { Select } from "shared/components";
import { states } from "utils/stateCodes";
import Drawer from "react-modern-drawer";
import { Tabs } from "shared/components/Tabs";
import { toast } from "react-toastify";
import { useMedia } from "shared/providers";

type Props = {
  showSettings: boolean;
  setShowSettings: (value: boolean) => void;
  property: Property;
  refetch: () => void;
  setShowDeletePropertyModal: (value: boolean) => void;
  selectedTemplateId: string;
  setSelectedTemplateId: (value: string) => void;
  setShowReviewModal: (value: boolean) => void;
};

export const PropertySettings: FC<Props> = ({
  showSettings,
  setShowSettings,
  property,
  refetch,
  setShowDeletePropertyModal,
  selectedTemplateId,
  setSelectedTemplateId,
  setShowReviewModal
}) => {
  const { register, onSubmit, values } = useForm({
    propertyName: property.name
  });

  const { type } = useAuthContext();

  const [tab, setActiveTab] = useState<
    "Screening Settings" | "Property Details"
  >(type.isBoomScreen ? "Screening Settings" : "Property Details");

  useEffect(() => {
    if (type.isBoomScreen && (type.isAdmin || type.isOwner))
      setActiveTab("Screening Settings");
    else setActiveTab("Property Details");
  }, [type.isBoomScreen]);

  const { templates } = useVerificationTemplatesController(999);

  const sortedTemplates = templates?.sort(
    (a, b) => Number(b.default) - Number(a.default)
  );

  const [, setSelectedPlace] = useState<google.maps.places.PlaceResult | null>(
    null
  );
  const [address, setAddress] = useState({
    address1: property.address.address1,
    address2: property.address.address2,
    postal_code: property.address.postal_code,
    city: property.address.city,
    state_or_province: property.address.state_or_province,
    country: property.address.country
  });

  const { ref } = usePlacesWidget({
    apiKey: config.placesApiKey,
    onPlaceSelected: (place) => {
      setSelectedPlace(place);

      const addressFields = googlePlaceToAddress(place);
      setAddress({
        address1: addressFields?.address1 || "",
        address2: address.address2,
        postal_code: addressFields?.postalCode,
        city: addressFields?.city,
        state_or_province: addressFields?.stateOrProvince,
        country: addressFields?.country
      });
    },
    options: {
      types: ["street_address"],
      fields: ["formatted_address", "address_components"],
      componentRestrictions: { country: "us" }
    }
  });

  useEffect(() => {
    if (property.verification_template)
      setSelectedTemplateId(String(property.verification_template.id));
  }, [property]);

  const { isMobile } = useMedia();

  return (
    <Drawer
      className="rounded-l-lg"
      direction="right"
      onClose={() => {
        setShowSettings(false);
      }}
      open={showSettings}
      size={isMobile ? 350 : 500}
      zIndex={20}
    >
      <div className="flex items-center justify-between px-6 py-4">
        <h1 className="text-xl font-bold text-content-1000">
          Property settings
        </h1>
        <IconButton
          variant={IconButtonVariant.BASE}
          size={IconButtonSize.SM}
          borderLess
          className="!justify-end"
          onClick={() => setShowSettings(false)}
          Icon={XClose}
        ></IconButton>
      </div>
      <div className="mb-2 border-b border-alpha-80" />

      {type.isPMS ? (
        <div className="mx-4">
          <span className="text-xs text-content-700 font-medium bg-alpha-40 px-2 py-0.5 rounded-3xl">
            {property.pms_id ? "PMS" : "Manually added"}
          </span>
        </div>
      ) : null}

      <form
        className="flex flex-col max-h-[calc(100vh-110px)] px-6 py-4"
        onSubmit={onSubmit({
          onSuccess: async () => {
            const data =
              type.isPMS && property.pms_id
                ? {
                    propertyId: property.id,
                    verification_template_id: selectedTemplateId
                  }
                : {
                    propertyId: property.id,
                    name: values.propertyName,
                    address: address,
                    verification_template_id: selectedTemplateId
                  };
            try {
              await addressApi.editProperty(data);
              setShowSettings(false);
              refetch();
            } catch (err) {
              toast.error(getErrorMessage(err));
            }
          }
        })}
      >
        <Tabs
          selected={tab}
          variant="underline"
          setActiveTab={(t) => {
            setActiveTab(t);
          }}
          tabs={
            type.isBoomScreen && (type.isAdmin || type.isOwner)
              ? ["Screening Settings", "Property Details"]
              : ["Property Details"]
          }
        />
        {tab === "Screening Settings" ? (
          <div
            className="mt-6 overflow-y-auto"
            style={{
              height: "calc(100dvh - 284px)"
            }}
          >
            <div className="grid grid-cols-2 gap-4">
              {sortedTemplates?.map((template) => (
                <CheckboxCard
                  key={template.id}
                  checked={selectedTemplateId === template.id}
                  id={template.id}
                  label={
                    <div className="relative w-full">
                      {template.default ? (
                        <p className="absolute -right-4 -top-4 rounded-bl-lg rounded-tr-xl border-b border-l border-alpha-100 bg-white px-2 text-xs font-medium">
                          Default
                        </p>
                      ) : null}
                      <p className="w-full overflow-hidden text-ellipsis whitespace-nowrap break-all text-sm font-medium text-content-600">
                        {template.name}
                      </p>
                      <Button
                        variant={ButtonVariant.TEXT}
                        onClick={() => {
                          setSelectedTemplateId(template.id);
                          setShowReviewModal(true);
                        }}
                      >
                        Review
                      </Button>
                    </div>
                  }
                  name="name"
                  onChange={() => setSelectedTemplateId(template.id)}
                  className="flex min-h-[106px] min-w-[207px] justify-start"
                />
              ))}
            </div>
          </div>
        ) : (
          <div
            className="mt-6 overflow-y-auto"
            style={{
              height: "calc(100dvh - 284px)"
            }}
          >
            <Input
              id="propertyName"
              label="Property Name"
              placeholder="Property Name"
              {...register("propertyName")}
              className="mb-4"
              readOnly={!!property.pms_id}
              required
            />
            <div>
              <Input
                id="selectedPlace"
                label="Street Address"
                value={address.address1}
                onChange={(e) =>
                  setAddress((prev) => ({
                    ...prev,
                    address1: e.target.value
                  }))
                }
                placeholder={property.address.address1}
                readOnly={!!property.pms_id}
                // @ts-expect-error
                ref={ref}
                required
                className="mb-4"
              />

              <div>
                <label className="mb-1 block text-sm font-[500] text-content-700">
                  Apartment, suite, etc.
                  <span className="ml-1 text-content-100">(optional)</span>
                </label>
                <Input
                  id="address2"
                  name="address2"
                  value={address.address2}
                  placeholder={property.address.address2}
                  onChange={(e) =>
                    setAddress((prev) => ({
                      ...prev,
                      [e.target.id]: e.target.value
                    }))
                  }
                  readOnly={!!property.pms_id}
                  className="mb-4"
                />
              </div>
              <Input
                id="city"
                name="city"
                label="City"
                value={address.city}
                placeholder={property.address.city}
                onChange={(e) =>
                  setAddress((prev) => ({
                    ...prev,
                    [e.target.id]: e.target.value
                  }))
                }
                readOnly={!!property.pms_id}
                required
                className="mb-4"
              />
              <div className="flex w-full gap-4">
                <div className="flex-1">
                  <div className="flex w-full gap-4">
                    <div className="mb-2 mt-auto h-fit w-full flex-1">
                      <Select
                        id="state_or_province"
                        label="State"
                        value={address.state_or_province}
                        searchable
                        hideOptional
                        placeholder={property.address.state_or_province}
                        options={states}
                        onChange={(e) =>
                          setAddress((prev) => ({
                            ...prev,
                            state_or_province: e.target.value
                          }))
                        }
                        isDisabled={!!property.pms_id}
                      />
                    </div>
                    <div className="flex-1">
                      <Input
                        id="postal_code"
                        name="postalCode"
                        label="Zip"
                        value={address.postal_code}
                        placeholder={property.address.postal_code}
                        onChange={(e) =>
                          setAddress((prev) => ({
                            ...prev,
                            [e.target.id]: e.target.value
                          }))
                        }
                        readOnly={!!property.pms_id}
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="mt-auto">
          <Button
            variant={
              !property.pms_id || !property.can_delete
                ? ButtonVariant.BASE
                : ButtonVariant.TEXT
            }
            size={ButtonSize.SM}
            className="mb-4 flex gap-2 h-[40px]"
            borderLess={!type.isPMS || !property.can_delete}
            onClick={() => {
              setShowDeletePropertyModal(true);
            }}
            Icon={Trash02}
            disabled={!!property.pms_id || !property.can_delete}
            tooltip={
              property.can_delete || !property.pms_id
                ? ""
                : "You can't delete a property with applications"
            }
            tooltipZIndex={21}
          >
            Delete property
          </Button>
          <div className="-mx-6 mb-4 border-b border-alpha-100" />
          <div className="flex gap-2">
            <div className="w-full">
              <Button
                variant={ButtonVariant.BASE}
                size={ButtonSize.SM}
                className="w-full"
                onClick={() => setShowSettings(false)}
              >
                Cancel
              </Button>
            </div>
            <div className="w-full">
              <Button
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.SM}
                className="w-full"
                type="submit"
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      </form>
    </Drawer>
  );
};
